define("tmds/renderers/UniqueValueRenderer", [
    "jquery",
    "./Renderer",
    "tmds/symbols/Symbol",
    "tmds/utils/symbolUtils",
    '../validator'
], function($, Renderer, Symbol, symUtils, validator) {
	"use strict";
	
	/**
	 * construct a new UniqueValueRenderer
	 * @param options {Object} object with below structure
	 * 		{
	 * 			fieldName: "valueType",		// REQUIRED
	 * 			fieldName1: "field2",		// Optional; second field name
	 * 			fieldDelimeter: ",",		// Optional; Applicable only when fieldName1 is specified for the renderer.
	 * 			defaultSymbol: {
	 * 				type: "SimplePointSymbol",
	 * 				style: 'square',
	 * 				color: [255,0,0,0.5],
	 * 				size: 12
	 * 			},
	 * 			uniqueValueInfos: [{
	 * 				value: "value1",
	 * 				symbol: {
	 * 					type: "SimplePointSymbol",
	 * 					style: 'square',
	 * 					color: [255,255,0,0.5],
	 * 					size: 12
	 * 				}
	 * 			},{
	 * 				value: "value2",
	 * 				symbol: {
	 * 					type: "SimplePointSymbol",
	 * 					style: 'square',
	 * 					color: [255,0,124,0.5],
	 * 					size: 12
	 * 				}
	 * 			}]
	 * 		}
	 * @note fieldName is required. Others are optional.
	 */
	var UniqueValueRenderer = function(options) {
		this.fieldName = "";
		this.defaultSymbol = symUtils.fromJson({
			type: "SimplePointSymbol",
			style: 'square',
			color: [255,0,124,0.5],
			size: 12
		});
		
		this.infos = [];
		
		if (validator.isDataObject(options) && validator.isString(options.fieldName)) {
			
			this.fieldName = options.fieldName;
		} else {
			throw new Error('UniqueValueRenderer constructor: fieldName is missing.');
		}
		
		if (validator.isDataObject(options) && validator.isString(options.fieldName1)) {
			
			this.fieldName1 = options.fieldName1;		
			this.fieldDelimeter = validator.isString(options.fieldDelimeter) ? options.fieldDelimeter : ",";

			if (validator.isString(options.fieldName2)) {
				this.fieldName2 = options.fieldName2;
			}
		}
		
		if (options.defaultSymbol != undefined) {
			
			if (! validator.isDataObject(options.defaultSymbol)) {
				
				throw new Error('Invalid or empty defaultSymbol object - ' + options.defaultSymbol + ' passed');
			}
			this.defaultSymbol = symUtils.fromJson(options.defaultSymbol);
		}
		
		if (options.uniqueValueInfos != undefined) {
			
			if (! validator.isArray(options.uniqueValueInfos)) {
				
				throw new Error('Invalid uniqueValueInfos - ' + options.uniqueValueInfos + ' passed');
			}
			var me = this;
			$.each(options.uniqueValueInfos, function(_, info) {
				me.infos.push({
					value: info.value,
					symbol: symUtils.fromJson(info.symbol)
				});
			});
		}
	};
	
	/**
	 * add a new value-symbol pair
	 * @param value {String | Number} field value
	 * @param symbol {tmds.symbols.Symbol} symbol used to render the feature with fieldName value = value.
	 * @return the renderer
	 * @note
	 * 	1. if this.fieldName not set, skip. 
	 * 	2. if the given value already exists in infos, update its symbol with the given symbol; otherwise, create a new entry.
	 */
	function addValue(value, symbol) {
		if (this.fieldName.trim()) {
			var item = _getItem.call(this, value);
			
			if (! validator.isInstanceOf(symbol, Symbol))
				throw new Error(symbol + ' is not an instance of Symbol');
			
			if (item) {
				this.infos[item.idx].symbol = symbol;
			}
			else {
				this.infos.push({
					value: value,
					symbol: symbol
				});
			}
		}
		
		return this;
	}
	
	/**
	 * remove from infos the item whose value equals given value
	 * @param value {String | Number} field value
	 * @return this renderer
	 * @note do nothing if no entry in infos matches the given value
	 */
	function removeValue(value) {
		var item = _getItem.call(this, value);
		
		if (item) {
			this.infos.splice(item.idx, 1);
		}
		
		return this;
	}
	
	/**
	 * get the symbol that associated with the given value
	 * @param value {String | Number} field value
	 * @return {tmds.symbols.Symbol} return null if no info matches the given value.
	 */
	function getSymbol(value) {
		var item = _getItem.call(this, value);
		
		return item ? item.info.symbol : this.defaultSymbol;
	}
	
	/**
	 * retrieve an info item by value
	 * @param value {String | Number} field value
	 * @return {Object} with the following structure
	 * 	{
	 * 		idx: the info idx in the infos array
	 * 		info: the found info object
	 * 	}
	 *  return null if no found info object
	 */
	function _getItem(value) {
		var idx,
			info = null;
		
		this.infos.some(function(aInfo, i) {
			if (aInfo.value === value) {
				info = aInfo;
				idx = i;
				return true;
			}
		});
		
		return info ? {	idx: idx, info: info } : null;
	}
	
	/**
	 * convert UniqueValueRenderer object to JSON representation
	 */
	function toJson() {
		var uniValueInfos = [];
		$.each(this.infos, function(i, info) {
			uniValueInfos.push({
				value: info.value,
				symbol: info.symbol.toJson()
			});
		});
		
		var jsonResult = {
			type: this.type,
			fieldName: this.fieldName,
			uniqueValueInfos: uniValueInfos,
			defaultSymbol : this.defaultSymbol.toJson()		
		};
		
		if (this.fieldName1 && this.fieldName1.trim()) {
			$.extend(jsonResult, {
				fieldName1: this.fieldName1,
				fieldDelimeter: this.fieldDelimeter
			});

			if (this.fieldName2 && this.fieldName2.trim()) {
				$.extend(jsonResult, {
					fieldName2: this.fieldName2
				});
			}
		}
		
		return jsonResult;
	}
	
	/**
	 * set the instance level methods
	 */
	UniqueValueRenderer.prototype = $.extend(Object.create(Renderer.prototype), {
		declaredClass: "tmds.renderers.UniqueValueRenderer",
		type: "UniqueValueRenderer",
		'addValue' : addValue,
		'removeValue' : removeValue,
		'getSymbol' : getSymbol,
		'toJson' : toJson
	});
	
	return UniqueValueRenderer;
});